/**
 * FlyOut web component for displaying overlay content.
 * @extends HTMLElement
 */
export default class FlyOut extends HTMLElement {
  /** @type {boolean} */
  isOpen;

  /** @type {HTMLElement | null} */
  activator;

  /** @type {HTMLElement | null} */
  overlay;

  /** @type {HTMLElement | null} */
  contentContainer;

  /** @type {HTMLElement | null} */
  darkenOverlay;

  constructor() {
    super();

    this.isOpen = false;
    this.activator = null;
    this.overlay = null;
    this.contentContainer = null;
    this.darkenOverlay = null;

    this.toggle = this.toggle.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
  }

  /**
   * Called when the element is added to the document.
   * @returns {void}
   */
  connectedCallback() {
    this.activator = this.querySelector('[fly-out-activator]');
    this.overlay = this.querySelector('[fly-out-overlay]');
    this.contentContainer = this.querySelector('[fly-out-content]');
    this.darkenOverlay = this.querySelector('[fly-out-darken-overlay]');

    this.assert(!!this.activator, 'No activator element found for fly out component.');
    this.assert(!!this.overlay, 'No overlay element found for fly out component.');
    this.assert(
      !!this.contentContainer,
      'No overlay container element found for fly out component.'
    );

    // Move this.overlay to be a direct child of the body element
    if (this.overlay && this.overlay.parentNode !== document.body) {
      if (this.overlay.parentNode) {
        this.overlay.parentNode.removeChild(this.overlay);
      }
      document.body.appendChild(this.overlay);
    }

    this.activator.addEventListener('click', this.toggle);
    this.overlay.addEventListener('click', this.handleOverlayClick);
  }

  /**
   * Called when the element is removed from the document.
   * @returns {void}
   */
  disconnectedCallback() {
    this.activator?.removeEventListener('click', this.toggle);
    this.overlay?.removeEventListener('click', this.handleOverlayClick);

    // Clean up overlay when component is removed
    if (this.overlay && this.overlay.parentNode === document.body) {
      document.body.removeChild(this.overlay);
    }
  }

  /**
   * Handles clicks on the overlay — closes on background click or close button click.
   * Uses event delegation for the close button so it survives innerHTML replacement.
   * @param {Event} e - The click event.
   * @returns {void}
   */
  handleOverlayClick(e) {
    const target = /** @type {HTMLElement} */ (e.target);

    if (
      target === this.overlay ||
      target === this.darkenOverlay ||
      target.closest('[fly-out-close]')
    ) {
      this.toggle();
    }
  }

  /**
   * Toggles the flyout open/closed state.
   * @returns {void}
   */
  toggle() {
    if (this.overlay?.classList.contains('open')) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Opens the flyout.
   * @returns {void}
   */
  open() {
    if (this.isOpen) return;
    this.overlay?.classList.add('open');
    this.contentContainer?.classList.add('open');
    this.isOpen = true;

    // This disables scrolling on body, while allowing scrolling only within the flyout
    document.body.style.overflow = 'hidden';
    //hide the shopfiy chat widget
    const chatWidget = document.querySelector('#ShopifyChat');
    if (chatWidget) chatWidget.style.opacity = '0';
  }

  /**
   * Closes the flyout.
   * @returns {void}
   */
  close() {
    if (!this.isOpen) return;
    this.overlay?.classList.remove('open');
    this.contentContainer?.classList.remove('open');
    this.isOpen = false;
    document.body.style.overflow = '';
    //show the shopfiy chat widget
    const chatWidget = document.querySelector('#ShopifyChat');
    if (chatWidget) chatWidget.style.opacity = '1';
  }

  /**
   * Asserts a condition is truthy, throwing an error if not.
   * @param {boolean} truthy - The condition to assert.
   * @param {string} message - The error message if assertion fails.
   * @returns {void}
   */
  assert(truthy, message) {
    if (truthy) return;

    throw new Error(message);
  }
}

if (!customElements.get('fly-out')) {
  customElements.define('fly-out', FlyOut);
}
